/**
 * Hit / miss / perfect particle bursts + ripple rings over the piano arena.
 */

type BurstKind = "hit" | "wrong" | "perfect";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  r: number;
  color: string;
  spin: number;
  rot: number;
}

interface Ripple {
  x: number;
  y: number;
  life: number;
  max: number;
  color: string;
}

export class HitParticleLayer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private particles: Particle[] = [];
  private ripples: Ripple[] = [];
  private running = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const cssW = parent.clientWidth || 800;
    const cssH = parent.clientHeight || 220;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  burst(kind: BurstKind, nx = 0.5, ny = 0.45): void {
    const w = this.canvas.width / this.dpr;
    const h = this.canvas.height / this.dpr;
    const x = nx * w;
    const y = ny * h;
    const n = kind === "perfect" ? 42 : kind === "hit" ? 28 : 18;
    const colors =
      kind === "perfect"
        ? ["#fff6a0", "#f0c15a", "#7dffd0", "#ffffff"]
        : kind === "hit"
          ? ["#7dffd0", "#2fe6b0", "#f0c15a", "#ffffff"]
          : ["#ff5a6a", "#ff8a5a", "#ffd0d0"];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 1.5 + Math.random() * (kind === "perfect" ? 7 : 5.5);
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 1.2,
        life: 1,
        max: 0.55 + Math.random() * 0.55,
        r: 1.5 + Math.random() * 3.5,
        color: colors[i % colors.length],
        spin: (Math.random() - 0.5) * 0.4,
        rot: Math.random() * Math.PI,
      });
    }
    if (kind === "perfect" || kind === "hit") {
      this.ripples.push({
        x,
        y,
        life: 1,
        max: kind === "perfect" ? 0.75 : 0.45,
        color: kind === "perfect" ? "rgba(240,193,90,0.85)" : "rgba(47,230,176,0.55)",
      });
    }
    this.start();
  }

  private start(): void {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      this.draw();
      if (this.particles.length === 0 && this.ripples.length === 0) {
        this.running = false;
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        return;
      }
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  private draw(): void {
    const w = this.canvas.width / this.dpr;
    const h = this.canvas.height / this.dpr;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);

    const nextR: Ripple[] = [];
    for (const r of this.ripples) {
      r.life -= 0.016 / r.max;
      if (r.life <= 0) continue;
      nextR.push(r);
      const p = 1 - r.life;
      ctx.beginPath();
      ctx.arc(r.x, r.y, 8 + p * 70, 0, Math.PI * 2);
      ctx.strokeStyle = r.color;
      ctx.globalAlpha = Math.max(0, r.life * 0.9);
      ctx.lineWidth = 2.5 * r.life;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    this.ripples = nextR;

    const next: Particle[] = [];
    for (const p of this.particles) {
      p.life -= 0.016 / p.max;
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.12;
      p.vx *= 0.98;
      p.rot += p.spin;
      if (p.life <= 0) continue;
      next.push(p);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color;
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 8;
      ctx.fillRect(-p.r, -p.r * 0.4, p.r * 2, p.r * 0.8);
      ctx.restore();
    }
    this.particles = next;
  }
}
